import {
  Box,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Center,
  Flex,
  FormControl,
  Select as ChakraSelect,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import { Link as ReactRouterLink } from "react-router-dom";
import { Select } from "chakra-react-select";
import { CherryPayApi } from "../../api/models";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { Card } from "../../components/Card/Card";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { FilterPanel } from "../../components/FilterPanel/FilterPanel";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { DateRangeField } from "../../components/DateRangeField/DateRangeField";
import { SearchInput } from "../../components/SearchInput/SearchInput";
import { PreviewButton } from "../../components/Utilities/PreviewButton";
import { TablePanel } from "../../components/TablePanel/TablePanel";
import { ResendConfirmationEmailButton } from "../../components/ResendConfirmationEmailModal/ResendConfirmationEmailButton";
import { ResendConfirmationEmailModal } from "../../components/ResendConfirmationEmailModal/ResendConfirmationEmailModal";
import { CornerDownRightIcon } from "../../styles/icons";
import { useBusinessContext } from "../../context/ModelContext";
import { useApiRequest } from "../../hooks/useApiRequest";
import { SubmissionFinalisationColumn } from "./SubmissionFinalisationColumn";
import { MembershipRequestStatusBadge } from "./MembershipRequestStatusBadge";
import { usePaginatedApiRequest } from "../../hooks/usePaginatedApiRequest";
import { useSearchInputState } from "../../hooks/useSearchInputState";
import DateUtil from "../../util/DateUtil";

const membershipRequestStatusOptions: {
  value: CherryPayApi.MembershipRequestStatus;
  label: string;
}[] = [
  { value: "IncompleteByUser", label: "Incomplete by User" },
  { value: "AwaitingStaffReview", label: "Awaiting Staff Review" },
  { value: "AcceptedApplication", label: "Accepted" },
  { value: "RejectedApplication", label: "Rejected" },
  { value: "InError", label: "In Error" },
  { value: "CreatedInstantly", label: "Created Instantly" },
];

export const MembershipRequest = () => {
  const business = useBusinessContext();
  const { query, debouncedQuery, onChange, isDebouncing, setQuery } =
    useSearchInputState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [registration, setRegistration] = useState<string>();
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [rowNumber, setRowNumber] = useState(10);
  const [isFinalised, setIsFinalised] = useState<boolean>();
  const [statuses, setStatuses] = useState<
    CherryPayApi.MembershipRequestStatus[]
  >([]);
  const [act, setAct] = useState(0);
  const [resendSessionId, setResendSessionId] = useState<string>();

  const run = () => {
    setAct((prev) => ++prev);
  };

  const clearAllFilters = () => {
    setQuery("");
    setShowDeleted(false);
    setRegistration(undefined);
    setStartDate(undefined);
    setEndDate(undefined);
    setRowNumber(10);
    setIsFinalised(undefined);
    setStatuses([]);
    run();
  };

  const {
    data: registrations,
    error: registrationsError,
    isLoading: registrationsLoading,
  } = useApiRequest(
    (apiClient) =>
      apiClient.getRegistrationFormConfigurationSummaryItems(
        business.BusinessId
      ),
    [business]
  );

  const {
    items: membershipRequests,
    isLoading,
    isLoadingPage,
    error,
    fetchNextPage,
  } = usePaginatedApiRequest(
    (apiClient) => {
      const start = DateUtil.setStartEndDate(startDate, "start");
      const end = DateUtil.setStartEndDate(endDate, "end");
      return apiClient.getMembershipRequests(business.BusinessId, {
        includeDeleted: showDeleted,
        q: debouncedQuery,
        startDate: start,
        endDate: end,
        limit: rowNumber,
        isFinalised,
        status: statuses.length ? statuses : undefined,
        registrationFormConfigurationId: registration,
      });
    },
    (apiClient, continuationToken) => {
      const start = DateUtil.setStartEndDate(startDate, "start");
      const end = DateUtil.setStartEndDate(endDate, "end");
      return apiClient.getMembershipRequests(business.BusinessId, {
        continuationToken: continuationToken,
        includeDeleted: showDeleted,
        q: debouncedQuery,
        startDate: start,
        endDate: end,
        limit: rowNumber,
        isFinalised,
        status: statuses.length ? statuses : undefined,
        registrationFormConfigurationId: registration,
      });
    },
    [act, debouncedQuery]
  );

  const selectedStatusOptions = useMemo(
    () =>
      membershipRequestStatusOptions.filter((option) =>
        statuses.includes(option.value)
      ),
    [statuses]
  );

  const programTypeOptions = useMemo(
    () =>
      registrations?.map((p) => {
        return (
          <option value={p.Meta?.Id!} key={p.Meta?.Id}>
            {p.DisplayName}
          </option>
        );
      }),
    [registrations]
  );

  const getSubmissionFinalisationTitle = (
    finalisation?: CherryPayApi.SubmissionFinalisation
  ) => {
    if (!finalisation || !finalisation.IsFinalised)
      return "This request has not been finalised";
    return `Finalised by ${finalisation.FinalisedByUser}\r\n${
      finalisation.AcceptanceDetails
    }\r\nNote: ${finalisation.RejectionNoteByUser ?? "None"}`;
  };

  // Session ID, Request Date, Patron Name, Member Number Issued, Status,
  // Membership Finalised, Actions (+ Application Form when there are several).
  const columnCount = 7 + (registrations && registrations.length > 1 ? 1 : 0);
  const isPageLoading = isLoading || registrationsLoading;

  const requestRows = useMemo(
    () =>
      (membershipRequests.length > 0 &&
        membershipRequests?.map((item) => {
          const formattedDate = DateUtil.getLocalDateFormatFromString(
            item.RequestSubmittedDateTime
          );
          return (
            <Tr key={item.SessionId}>
              {registrations && registrations.length > 1 && (
                <Td maxW="180px" whiteSpace="normal" wordBreak="break-word">
                  {item.RegistrationFormConfigurationName}
                </Td>
              )}
              <Td maxW="160px" whiteSpace="normal" wordBreak="break-word">
                {item.SessionId}
              </Td>
              <Td whiteSpace="nowrap">{formattedDate}</Td>
              <Td>
                {/* <Link
                as={ReactRouterLink}
                to={`/businesses/${business.BusinessId}/membership-requests/${item.SessionId}`}
              > */}
                {item.DisplayName}
                {/* </Link> */}
              </Td>
              <Td whiteSpace="nowrap">
                {item.MembershipNumber && (
                  <PreviewButton
                    hover={true}
                    color="gray.600"
                    linkProps={{ target: "_self" }}
                    url={`/businesses/${business.BusinessId}/members/${item.MemberId}`}
                  >
                    {item.MembershipNumber}
                  </PreviewButton>
                )}
              </Td>
              <Td title={item.Message ?? "-"}>
                <Flex direction="column" alignItems="flex-start">
                  <MembershipRequestStatusBadge status={item.Status} />
                  {(item.Status === "IncompleteByUser" ||
                    item.Status === "Incomplete by User") &&
                    item.NextStep && (
                      <Flex
                        alignItems="center"
                        mt={2}
                        color="gray.500"
                        fontSize="xs"
                        whiteSpace="nowrap"
                      >
                        <CornerDownRightIcon boxSize={3.5} mr={1} />
                        <Box as="span">{item.NextStep}</Box>
                      </Flex>
                    )}
                </Flex>
              </Td>
              <Td
                title={getSubmissionFinalisationTitle(
                  item.SubmissionFinalisation
                )}
              >
                <SubmissionFinalisationColumn item={item} />
              </Td>
              <Td>
                <ResendConfirmationEmailButton
                  onClick={() =>
                    setResendSessionId(item.SessionId ?? undefined)
                  }
                />
              </Td>
            </Tr>
          );
        })) || (
        <Tr>
          <Td colSpan={columnCount} textAlign="center">
            <i>No Membership Request</i>
          </Td>
        </Tr>
      ),
    [membershipRequests, registrations, business, columnCount]
  );
  return (
    <>
      <PageHeading>
        <BusinessBreadcrumbs>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={ReactRouterLink}
              to={`/businesses/${business.BusinessId}/membership-requests`}
            >
              Membership Requests
            </BreadcrumbLink>
          </BreadcrumbItem>
        </BusinessBreadcrumbs>
        <PageHeading.Title>Membership Requests</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="start" w="100%" spacing={6}>
          <FilterPanel>
            <Flex gridGap={6} className="form-filter-wrapper">
              <Box>
                <SearchInput
                  value={query}
                  onChange={onChange}
                  isLoading={isLoading || isDebouncing}
                  placeholder="Search requests..."
                  setQuery={setQuery}
                />
              </Box>
              {registrations && registrations.length > 1 && (
                <Box>
                  <FormControl>
                    <ChakraSelect
                      placeholder="All application forms"
                      bg="white"
                      onChange={(e) =>
                        setRegistration(e.target.value || undefined)
                      }
                      value={registration ?? ""}
                    >
                      {programTypeOptions}
                    </ChakraSelect>
                  </FormControl>
                </Box>
              )}
              <Box minW="320px">
                <FormControl>
                  <Select
                    isMulti
                    placeholder="Filter by Status"
                    options={membershipRequestStatusOptions}
                    value={selectedStatusOptions}
                    onChange={(selectedOptions) =>
                      setStatuses(
                        selectedOptions?.map(
                          (option) =>
                            option.value as CherryPayApi.MembershipRequestStatus
                        ) ?? []
                      )
                    }
                    closeMenuOnSelect={false}
                    selectedOptionColor="blue"
                    chakraStyles={{
                      control: (provided) => ({
                        ...provided,
                        bg: "white",
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        bg: "white",
                      }),
                      indicatorSeparator: () => ({
                        display: "none",
                      }),
                      multiValue: (provided) => ({
                        ...provided,
                        borderRadius: "full",
                      }),
                    }}
                  />
                </FormControl>
              </Box>
              {registrationsLoading && (
                <Center>
                  <Spinner />
                </Center>
              )}
              {registrationsError && (
                <ErrorMessage>
                  An error was encountered while loading the program types.
                </ErrorMessage>
              )}
              <Box>
                <DateRangeField
                  startDate={startDate}
                  endDate={endDate}
                  onStartDateChange={setStartDate}
                  onEndDateChange={setEndDate}
                />
              </Box>
              <Box>
                <FormControl>
                  <ChakraSelect
                    bg="white"
                    onChange={(e) =>
                      setIsFinalised(
                        !e.target.value
                          ? undefined
                          : e.target.value === "true"
                          ? true
                          : false
                      )
                    }
                    value={
                      isFinalised === undefined
                        ? ""
                        : isFinalised
                        ? "true"
                        : "false"
                    }
                  >
                    <option value="">All</option>
                    <option value="true">Finalised</option>
                    <option value="false">Not Finalised</option>
                  </ChakraSelect>
                </FormControl>
              </Box>
              <Box>
                <FormControl>
                  <ChakraSelect
                    bg="white"
                    onChange={(e) => setRowNumber(parseInt(e.target.value))}
                    value={rowNumber}
                    width="20"
                  >
                    <option value="10">10</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="250">250</option>
                    <option value="500">500</option>
                  </ChakraSelect>
                </FormControl>
              </Box>

              {/* <Box p="4" pt={12}>
              <Checkbox
                size="lg"
                onChange={() => setShowDeleted((prev) => !prev)}
                isChecked={showDeleted}
              >
                Show Deleted
              </Checkbox>
            </Box> */}
              <Box className="form-filter-button-wrapper">
                <Flex gridGap={4}>
                  <Button
                    variant="outline"
                    borderRadius="full"
                    bg="white"
                    onClick={clearAllFilters}
                  >
                    Clear All
                  </Button>
                  <Button
                    variant="solid"
                    colorScheme="cherryButton"
                    onClick={run}
                  >
                    Search
                  </Button>
                </Flex>
              </Box>
            </Flex>
          </FilterPanel>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {isPageLoading && (
            <Center>
              <Spinner margin="4" />
            </Center>
          )}
          {!isPageLoading && (
            <TablePanel>
              <TableContainer overflowX="auto">
                <Table w="100%" size="sm">
                  <Thead>
                    <Tr>
                      {registrations && registrations.length > 1 && (
                        <Th maxW="180px" whiteSpace="normal">
                          Application Form
                        </Th>
                      )}
                      <Th maxW="160px" whiteSpace="normal">
                        Session ID
                      </Th>
                      <Th>Request Date</Th>
                      <Th>Patron Name</Th>
                      <Th whiteSpace="nowrap">Member Number Issued</Th>
                      <Th>Status</Th>
                      <Th>Membership Finalised</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>{requestRows}</Tbody>
                </Table>
              </TableContainer>
            </TablePanel>
          )}
          {!isPageLoading && fetchNextPage && (
            <Card w="100%">
              {!isLoadingPage && (
                <Button colorScheme="cherryButton" onClick={fetchNextPage}>
                  Load more...
                </Button>
              )}
              {isLoadingPage && <Spinner />}
            </Card>
          )}
        </VStack>
      </PageContent>
      <ResendConfirmationEmailModal
        requestType="membership"
        sessionId={resendSessionId}
        isOpen={!!resendSessionId}
        onClose={() => setResendSessionId(undefined)}
      />
    </>
  );
};
